import { SagaIterator } from 'redux-saga';
import {
  call,
  put,
  takeEvery
  } from 'redux-saga/effects';
import _ from 'underscore';

import { requestSpaceContents } from '~src/app/actions/spaces/RequestSpaceContentsActions';
import { reloadSpaceIfUpdatingCurrentSpace } from '~src/app/sagas/spaces/content/RefreshContentSagas';
import { createMultipleItemResultNotificationValues, MultipleItemResultNotificationValues } from '~src/commonInfra/contentActions/ActionMessages';
import { ContentActionNotification } from '~src/commonInfra/contentActions/ContentActionNotification';
import { IErrorItem, MultipleItemResult } from '~src/commonInfra/contentActions/MultipleItemResult';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { CommonErrorCode, DataAccelerationErrorCode } from '~src/ts/helpers/ErrorCodes';
import { ViewContentType } from '~src/ts/models/ContentTypes';
import { IContentInfo } from '~src/ts/models/IContentInfo';
import { Views } from '~src/views/resources/Views';
import { IWorkbookInfo } from '~src/workbook/models/IWorkbookInfo';

import { DataAcceleration } from '../../app/actions/AppActionTypes';
import { requestFailed } from '../../app/actions/RequestFailedAction';
import { Formatters, Messages } from '../../codegen/Localize';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { WorkbookContentType } from '../../ts/models/ContentTypes';
import { Set } from '../../ts/models/Set';
import { DataAccelerationAction, DataAccelerationType } from '../actions/DataAccelerationEntityActions';
import { ViewDataAccelerationState } from '../types/IViewAcceleration';

function processDataAccelerationAction(contents: Set<IContentInfo>, dataAccelerationType: DataAccelerationType): Promise<IResult> {
  if (contents.size() < 1) {
    return null;
  }

  if (dataAccelerationType === DataAccelerationType.turnOnAcceleration) {
    return Views.turnOnDataAccelerationForViews(contents.keys());
  } else {
    return Views.turnOffDataAccelerationForViews(contents.keys());
  }
}

function getViewsForWorkbook(workbook: IWorkbookInfo): Promise<IResult> {
  const viewsQuery = Views.forWorkbook(workbook);

  return viewsQuery.getCount()
    .then((viewCount) => viewsQuery.getSlice(0, viewCount))
    .then((result) => result.items) as Promise<any>;
}

export function showNotification(result: IResult, contents: IContentInfo[], dataAccelerationType: DataAccelerationType) {
  const notification: MultipleItemResultNotificationValues = createMultipleItemResultNotificationValues(
    new MultipleItemResult(Set.fromIdItemArray(contents), result),
    {
      describeSuccessSummary: (successCount: number) => {
        return getSuccessSummary(successCount, dataAccelerationType);
      },
      describeErrorSummary: (errorCount: number) => {
        return getErrorSummary(errorCount, dataAccelerationType);
      },
      describeErrorDetail: (errorItem: IErrorItem<IContentInfo>) => {
        return {
          itemName: errorItem.item.name,
          errorMessage: getErrorMessageFor(errorItem.error.code)
        };
      }
    }
  );
  ContentActionNotification.displayItemizedToastsForResult(notification);
}

export function getSuccessSummary(successCount: number, dataAccelerationType: DataAccelerationType): string {
  if(dataAccelerationType === DataAccelerationType.turnOnAcceleration) {
    return Messages.turnOnDataAccelerationForViewsSuccess({ITEM_COUNT: successCount, ITEM_COUNT_LOC: Formatters.formatCount(successCount)});
  }

  return Messages.turnOffDataAccelerationForViewsSuccess({ITEM_COUNT: successCount, ITEM_COUNT_LOC: Formatters.formatCount(successCount)});
}

export function getErrorSummary(errorCount: number, dataAccelerationType: DataAccelerationType): string {
  if(dataAccelerationType === DataAccelerationType.turnOnAcceleration) {
    return Messages.turnOnDataAccelerationForViewsError({ERROR_COUNT: errorCount, ERROR_COUNT_LOC: Formatters.formatCount(errorCount)});
  }
  return Messages.turnOffDataAccelerationForViewsError({ERROR_COUNT: errorCount, ERROR_COUNT_LOC: Formatters.formatCount(errorCount)});
}

export function getErrorMessageFor(errorCode: number): string {
  switch (errorCode) {
    case DataAccelerationErrorCode.MISSING_CREDENTIALS:
      return Messages.dataAccelerationMissingCredential_errorMessage();
    case DataAccelerationErrorCode.SHEET_LIMIT_REACHED:
      return Messages.dataAccelerationSheetLimitReached_errorMessage();
    case DataAccelerationErrorCode.CONTAINS_ADMIN_DISALLOWED_SHEETS:
      return Messages.dataAccelerationContainsDisallowedSheets_errorMessage();
    case DataAccelerationErrorCode.DATA_FRESHNESS_INTERVAL_TOO_SHORT:
      return Messages.dataAccelerationDataFreshnessIntervalTooShort_errorMessage();
    case DataAccelerationErrorCode.ENCRYPTED_EXTRACTS_NOT_SUPPORTED:
      return Messages.dataAccelerationEncryptionNotSupported_errorMessage();
    case CommonErrorCode.CallingUserInsufficientPermissions:
      return Messages.dataAccelerationUserNotPermitted_errorMessage();
    default:
      return Messages.errorUnknown();
  }
}

function getErrorsForTurnOnDisallowedViews(disallowedContents: IContentInfo[]): IError[] {
  let errors: IError[] = [];
  disallowedContents.forEach(element => {
    errors.push({code: DataAccelerationErrorCode.CONTAINS_ADMIN_DISALLOWED_SHEETS, id: element.id});
  });
  return errors;
}

function processAccelerationContentsResults(contentsResult: IResult, disallowedErrors: IError[]): IResult {
  let result: IResult = {errors: []};

  if(contentsResult != null) {
    result.errors.push(...contentsResult.errors);
  }
  result.errors.push(...disallowedErrors);

  return result;
}

export function* handleDataAccelerationAction(action: DataAccelerationAction): SagaIterator {
  try {
    const contentDictionaryByType = _.groupBy(action.selectedContent, c => c.contentType);
    const typeArray = Object.keys(contentDictionaryByType);

    if (typeArray.length === 1 && (typeArray[0] === ViewContentType.name || typeArray[0] === WorkbookContentType.name)) {
      let contentsArray = contentDictionaryByType[typeArray[0]];

      if (typeArray[0] === WorkbookContentType.name){
        if (contentsArray.length === 1) {
          contentsArray = yield call(getViewsForWorkbook, contentsArray[0] as any);
        } else {
          // should never come to here as the action is only visible in workbook space
          return;
        }
      }

      const disallowedContentsArray: IContentInfo[] = [];
      const disallowedErrors: IError[] = [];
      // filter out those items that does not need to take any action on server
      if (action.dataAccelerationType === DataAccelerationType.turnOnAcceleration) {

        // those views that disallowed by admin need to send toast error message
        disallowedContentsArray.push(...contentsArray.filter(i => !i.acceleration.allowedByAdmin));
        disallowedErrors.push(...getErrorsForTurnOnDisallowedViews(disallowedContentsArray));

        // those views that send to server to turn on acceleration
        contentsArray = contentsArray.filter(i => i.acceleration.accelerationState === ViewDataAccelerationState.Off && i.acceleration.allowedByAdmin);
      } else {
        contentsArray = contentsArray.filter(i => i.acceleration.accelerationState !== ViewDataAccelerationState.Off);
      }

      const contents = Set.fromIdItemArray(contentsArray);
      const contentsResult = yield call(processDataAccelerationAction, contents, action.dataAccelerationType);

      let processedContents: IContentInfo[] = [...contentsArray, ...disallowedContentsArray];
      const processedContentsResult: IResult = processAccelerationContentsResults(contentsResult, disallowedErrors);

      if (processedContents.length >= 1) {
        yield call(showNotification, processedContentsResult, processedContents, action.dataAccelerationType);
        const didReload = yield call(reloadSpaceIfUpdatingCurrentSpace, contentsArray);
        if (!didReload) {
          yield put(requestSpaceContents());
        }
      }
    } else if (typeArray.length === 1) {
      yield put(requestFailed(`Data acceleration does not support ${typeArray[0]}.`, Messages.dataAccelerationOnlySupportsWorkbooksAndViews_errorMessage()));
    } else {
      yield put(requestFailed(`Data acceleration only supports one content type in a call.`, Messages.dataAccelerationOnlySupportsOneContentTypeInACall_errorMessage()));
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootDataAccelerationSaga(): SagaIterator {
  yield takeEvery(DataAcceleration, handleDataAccelerationAction);
}
